import { type QueryFunctionContext, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { carryOverRebuilding, hasRebuildingCells, REBUILDING_POLL_MS } from '@/api/rebuildingCells';
import { calendarApi } from '../api/calendarApi';
import { filterParamsKey, type CalendarQueryParams } from '../api/calendarFilterParams';
import type { CalendarDayResponse } from '../api/types';
import { keepWhenOnlyFiltersChange } from './keepWhenOnlyFiltersChange';

export function useCalendarDay(
  locationId: number | null | undefined,
  date: string,
  options?: { enabled?: boolean; filters?: CalendarQueryParams },
) {
  const queryClient = useQueryClient();
  const filters = options?.filters ?? {};
  const queryKey = keys.calendar.day(locationId ?? 0, date, filterParamsKey(filters));
  return useQuery({
    queryKey,
    queryFn: async ({ signal }: QueryFunctionContext) => {
      const next = await calendarApi.getDay(locationId!, date, filters, { signal });
      const prev = queryClient.getQueryData<CalendarDayResponse>(queryKey);
      const cells = carryOverRebuilding(next.cells, prev?.cells, (c) => c.appointment_id);
      if (!prev || !hasRebuildingCells(next.cells)) return { ...next, cells };
      // Columns are derived from the cells, so a day that is rebuilding arrives with none.
      return {
        ...next,
        cells,
        providers: next.providers.length > 0 ? next.providers : prev.providers,
        operatories: next.operatories.length > 0 ? next.operatories : prev.operatories,
      };
    },
    enabled: (options?.enabled ?? true) && locationId != null && !!date,
    staleTime: 90_000,
    // Opening the calendar always shows what the server has now, not a copy up to 90s old.
    refetchOnMount: 'always',
    // Cells still being rebuilt after an update are hidden — check back until they are ready.
    refetchInterval: (query) => (hasRebuildingCells(query.state.data?.cells) ? REBUILDING_POLL_MS : false),
    // A changed filter keeps the current grid on screen while it loads, instead of a full-page spinner.
    placeholderData: keepWhenOnlyFiltersChange<Awaited<ReturnType<typeof calendarApi.getDay>>>(queryKey),
  });
}
